import { Navigate, useNavigate, useParams } from 'react-router-dom'

import {
  FBPageInnerTabsContainer,
  FBPageTableContainer,
} from 'components/FbUI/Page/styles'
import { UnderlineTabs } from 'components/FbUI/UnderlineTabs'
import { SalesStageTable } from 'features/settings/components/SalesStageTable'
import { DistributorTable } from 'features/settings/components/DistributorTable'
import { GPOTable } from 'features/settings/components/GPOTable'
import { CaptureFormsTable } from 'components/CaptureForms/CaptureFormsTable'
import { useFeatureFlagService } from 'utils/useFeatureFlagService'
import { useAuth } from 'context/authentication/useAuth'

type PlatformTab = 'sales-stages' | 'distributors' | 'gpos' | 'capture-forms'

const DEFAULT_TAB: PlatformTab = 'sales-stages'

export function PlatformSettingsPage() {
  const params = useParams()
  const navigate = useNavigate()
  const { enableCaptureForms } = useFeatureFlagService()
  const { isAdmin } = useAuth()

  // Capture Forms is admin-only and gated by the enable_capture_forms flag.
  const canSeeCaptureForms = enableCaptureForms && isAdmin

  const tabs: { key: PlatformTab; title: string }[] = [
    { key: 'sales-stages', title: 'Sales Stages' },
    { key: 'distributors', title: 'Distributors' },
    { key: 'gpos', title: 'GPOs' },
    ...(canSeeCaptureForms
      ? [{ key: 'capture-forms' as PlatformTab, title: 'Capture Forms' }]
      : []),
  ]
  const validTabs = new Set(tabs.map((t) => t.key))
  const tab = (params.tab as PlatformTab) ?? DEFAULT_TAB

  if (params.tab && !validTabs.has(params.tab as PlatformTab)) {
    return <Navigate to={`/settings/platform/${DEFAULT_TAB}`} replace />
  }

  return (
    <div className="w-full h-full flex flex-col flex-1 min-h-0 bg-surface-page">
      <FBPageInnerTabsContainer>
        <UnderlineTabs<PlatformTab>
          tabs={tabs}
          activeKey={tab}
          onChange={(value) => navigate(`/settings/platform/${value}`)}
          idPrefix="platform-settings"
          aria-label="Platform settings tabs"
        />
      </FBPageInnerTabsContainer>
      <FBPageTableContainer
        className="bg-surface-page"
        hide={tab !== 'sales-stages'}
      >
        <SalesStageTable />
      </FBPageTableContainer>
      <FBPageTableContainer
        className="bg-surface-page"
        hide={tab !== 'distributors'}
      >
        <DistributorTable />
      </FBPageTableContainer>
      <FBPageTableContainer className="bg-surface-page" hide={tab !== 'gpos'}>
        <GPOTable />
      </FBPageTableContainer>
      {canSeeCaptureForms && (
        <FBPageTableContainer
          className="bg-surface-page"
          hide={tab !== 'capture-forms'}
        >
          <CaptureFormsTable />
        </FBPageTableContainer>
      )}
    </div>
  )
}
